import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import './AdminOverview.css';

function AdminOverview() {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    users: { total: 0, active: 0, inactive: 0, new_last_30_days: 0, new_last_7_days: 0 },
    roles: { total: 0, distribution: [] },
    departments: { total: 0, active: 0, distribution: [] },
    schedules: { total: 0, today: 0, next_7_days: 0 }
  });

  // Fetch admin metrics
  useEffect(() => {
    fetchAdminMetrics();

    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchAdminMetrics, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchAdminMetrics = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/admin/metrics/overview'));
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching admin metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-overview">
      {/* Key Metrics Cards - 2 rows of 3 */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">👥</span>
            <span className="kpi-label">{t('overview.kpis.totalUsers')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.users.total}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{metrics.users.active} {t('overview.labels.active')}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">✨</span>
            <span className="kpi-label">{t('overview.kpis.newUsers')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.users.new_last_7_days}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('overview.labels.last7days')}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">🔑</span>
            <span className="kpi-label">{t('overview.kpis.userRoles')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.roles.total}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('overview.labels.definedRoles')}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">🏢</span>
            <span className="kpi-label">{t('overview.kpis.departments')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.departments.active}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{metrics.departments.total} {t('overview.labels.total')}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">📅</span>
            <span className="kpi-label">{t('overview.kpis.schedulesToday')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.schedules.today}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{metrics.schedules.total} {t('overview.labels.total')}</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-header">
            <span className="kpi-icon">📆</span>
            <span className="kpi-label">{t('overview.kpis.next7Days')}</span>
          </div>
          <div className="kpi-value">{loading ? '...' : metrics.schedules.next_7_days}</div>
          <div className="kpi-footer">
            <div className="kpi-change">{t('overview.labels.upcomingSchedules')}</div>
          </div>
        </div>
      </div>

      {/* Role Distribution */}
      {!loading && metrics.roles.distribution.length > 0 && (
        <div className="customers-analytics">
          <div className="section-header">
            <span className="section-icon">🔑</span>
            <h3>{t('overview.sections.roleDistribution')}</h3>
          </div>
          <div className="customers-table">
            {metrics.roles.distribution.map((role) => (
              <div key={role.role_code} className="customer-row">
                <div className="customer-details">
                  <span className="customer-name">{role.role_name}</span>
                  <span className="customer-type">{role.role_code}</span>
                </div>
                <div className="orders-count">{role.user_count} {t('overview.labels.users')}</div>
                <div className="revenue-amount">
                  {metrics.users.total > 0 ? Math.round((role.user_count / metrics.users.total) * 100) : 0}{t('overview.labels.percent')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Department Distribution */}
      {!loading && metrics.departments.distribution.length > 0 && (
        <div className="customers-analytics">
          <div className="section-header">
            <span className="section-icon">🏢</span>
            <h3>{t('overview.sections.departmentDistribution')}</h3>
          </div>
          <div className="customers-table">
            {metrics.departments.distribution.map((dept) => (
              <div key={dept.department_code} className="customer-row">
                <div className="customer-details">
                  <span className="customer-name">{dept.department_name}</span>
                  <span className="customer-type">{dept.department_code}</span>
                </div>
                <div className="orders-count">{dept.user_count} {t('overview.labels.users')}</div>
                <div className="revenue-amount">
                  {metrics.users.total > 0 ? Math.round((dept.user_count / metrics.users.total) * 100) : 0}{t('overview.labels.percent')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOverview;
